import { TENIS10 } from "./tenis10Event"

export const DEFAULT_EVENT_LIST_OPTIONS = {
  page: 1,
  limit: 15,
}

export const DEFAULT_EVENT_TIMEFRAME = "current"
export const EVENT_LIST_SEARCH_MIN_LENGTH = 2
export const GLOBAL_SEARCH_TIMEFRAME = ""

const sortEntries = value =>
  Object.entries(value || {}).sort(([keyA], [keyB]) => keyA.localeCompare(keyB))

export const normalizeEventsListOptions = options => {
  const page = Number(options?.page)
  const limit = Number(options?.limit)

  return {
    page: page > 0 ? page : DEFAULT_EVENT_LIST_OPTIONS.page,
    limit: limit > 0 ? limit : DEFAULT_EVENT_LIST_OPTIONS.limit,
  }
}

export const resetEventsListPage = options => ({
  ...normalizeEventsListOptions(options),
  page: 1,
})

export const normalizeEventsListFilters = filters =>
  sortEntries(filters).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === "") return acc

    if (key === "search") {
      const search = String(value).trim()
      if (search) acc.search = search
      return acc
    }

    acc[key] = value
    return acc
  }, {})

export const removeEventsSearchFilter = filters => {
  const { search, ...rest } = normalizeEventsListFilters(filters)
  return rest
}

export const getEventsLocationFilters = filters =>
  removeEventsSearchFilter(filters)

export const createEventsListQueryKey = value => {
  if (!value || typeof value !== "object") return JSON.stringify(value)

  return JSON.stringify(
    sortEntries(value).reduce((acc, [key, item]) => {
      acc[key] =
        item && typeof item === "object" && !Array.isArray(item)
          ? JSON.parse(createEventsListQueryKey(item))
          : item
      return acc
    }, {})
  )
}

const getSearchText = filters => String(filters?.search || "").trim()

export const shouldFetchEventsList = filters => {
  const search = getSearchText(filters)
  return !search || search.length >= EVENT_LIST_SEARCH_MIN_LENGTH
}

export const getEventsLocationTimeframe = ({
  eventTimeframe,
  filters,
  lastNonSearchTimeframe,
}) => {
  if (eventTimeframe === GLOBAL_SEARCH_TIMEFRAME && getSearchText(filters)) {
    return lastNonSearchTimeframe || DEFAULT_EVENT_TIMEFRAME
  }

  return eventTimeframe
}

export const applyEventsTimeframeChange = ({
  filters,
  options,
  timeframe,
}) => ({
  filters: removeEventsSearchFilter(filters),
  lastNonSearchTimeframe: timeframe,
  options: resetEventsListPage(options),
  searchText: "",
  timeframe,
})

export const applyEventsFilterChange = ({ nextFilters, options }) => ({
  filters: normalizeEventsListFilters(nextFilters),
  options: resetEventsListPage(options),
})

export const applyEventsSearchChange = ({
  eventTimeframe,
  filters,
  lastNonSearchTimeframe,
  options,
  searchText,
}) => {
  const inputText = String(searchText || "")
  const search = inputText.trim()
  const restoredTimeframe =
    eventTimeframe === GLOBAL_SEARCH_TIMEFRAME
      ? lastNonSearchTimeframe || DEFAULT_EVENT_TIMEFRAME
      : eventTimeframe

  if (search && search.length < EVENT_LIST_SEARCH_MIN_LENGTH) {
    return { inputText, shouldUpdate: false }
  }

  if (!search) {
    return {
      filters: removeEventsSearchFilter(filters),
      inputText,
      lastNonSearchTimeframe: restoredTimeframe,
      options: resetEventsListPage(options),
      searchText: "",
      shouldUpdate: true,
      timeframe: restoredTimeframe,
    }
  }

  return {
    filters: {
      ...removeEventsSearchFilter(filters),
      search,
    },
    inputText,
    lastNonSearchTimeframe: restoredTimeframe,
    options: resetEventsListPage(options),
    searchText: search,
    shouldUpdate: true,
    timeframe: GLOBAL_SEARCH_TIMEFRAME,
  }
}

// The Sport filter's choice as Events reads it (P43, Tudor 2026-10-24): Tennis is the adults' events alone (programme `sportya`,
// Events' programTypeFilter) and Tenis10 the children's programme of tennis; padel has no programme. The address keeps the one choice.
const SPORT_CHOICES = { tennis: { sportType: "tennis", programType: "sportya" }, [TENIS10]: { sportType: "tennis", programType: TENIS10 } }
const sportRead = ({ sportType, ...filters } = {}) => sportType ? { ...filters, ...(SPORT_CHOICES[sportType] || { sportType }) } : filters

export const createEventsListUrl = ({ filters, options, timeframe }) => {
  const normalizedFilters = normalizeEventsListFilters(sportRead(filters))
  const normalizedOptions = normalizeEventsListOptions(options)
  const filterParams = new URLSearchParams({
    ...normalizedFilters,
    ...normalizedOptions,
  }).toString()

  return `/admin/events?${filterParams}&timeframe=${timeframe || ""}`
}

// The Location filter matches club.location.city exactly, so its options must be the city
// spellings stored on the events themselves (e.g. "București"), not the global city catalogue
// behind /events/locations?allLocations=true, which spells them in English ("Bucharest").
export const createEventsLocationsUrl = () =>
  "/admin/events/facets"
